import React, { createContext, useState, useContext, useEffect } from "react"
import { MobergTheme, MobergThemeColors, getColors } from "../Moberg"
import { ColorScheme } from "../Components/MobergThemes/MobergColorTokens"
import { useEnvironmentVariablesProvider } from "./EnvironmentVariablesProvider"

type MobergThemeContextType = {
	colors: MobergThemeColors
	setTheme: React.Dispatch<React.SetStateAction<MobergTheme>>
	colorScheme: ColorScheme
	setColorScheme: React.Dispatch<React.SetStateAction<ColorScheme>>
}

const MobergThemeContext = createContext<MobergThemeContextType>({} as MobergThemeContextType)

const THEME_STORAGE_KEY = "moberg-color-scheme"

const getInitialColorScheme = (): ColorScheme => {
	try {
		const stored = localStorage.getItem(THEME_STORAGE_KEY)
		if (stored === "dark" || stored === "light") return stored
	} catch {}
	return "light"
}

export const MobergThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { environmentVariables } = useEnvironmentVariablesProvider()
	const [theme, setTheme] = useState<MobergTheme>(MobergTheme.BLUE)
	const [colorScheme, setColorScheme] = useState<ColorScheme>(getInitialColorScheme)

	// If the dark-mode feature flag is off for this workspace, force light
	// so users can't get stranded in dark mode after the flag is disabled.
	useEffect(() => {
		if (environmentVariables?.FEATURE_FLAG_DARK_MODE === false && colorScheme === "dark") {
			setColorScheme("light")
		}
	}, [environmentVariables?.FEATURE_FLAG_DARK_MODE, colorScheme])

	useEffect(() => {
		document.documentElement.setAttribute("data-color-scheme", colorScheme)
		try { localStorage.setItem(THEME_STORAGE_KEY, colorScheme) } catch {}
	}, [colorScheme])

	return (
		<MobergThemeContext.Provider value={{ colors: getColors(theme), setTheme, colorScheme, setColorScheme }}>
			{children}
		</MobergThemeContext.Provider>
	)
}

export const useMobergTheme = () => useContext(MobergThemeContext)
